// The create-flow engine every "New …" page uses (ui-spec §3.5, §4.17, ADR-031, ADR-030, ADR-107):
// the manifest is the source of truth and a browser-local draft; a debounced dry-run returns the estimate and its
// ETag; Launch sends If-Match on that ETag and an Idempotency-Key generated once per submission and kept with the
// draft, so a retry after a timeout can never create a second object.
import { ApiError, classify, collectionPath, kindByShortName, newIdempotencyKey, objectPath, useApi, type KObject } from "@nodus/api";
import { duplicateOf, generatedName, type EstimateView, type Manifest, type OfferingLike } from "@nodus/ui";
import { useQuery } from "@tanstack/react-query";
import * as React from "react";

interface Submission { manifest: Manifest; idempotencyKey: string; etag: string }

interface Draft {
  manifest: Manifest;
  idempotencyKey: string;
  savedAt: string;
  submitted?: Submission;
}

export type LaunchState =
  | { state: "idle" }
  | { state: "launching" }
  | { state: "done"; object: KObject }
  | { state: "repriced" }
  | { state: "uncertain"; requestId?: string }
  | { state: "error"; error: unknown };

function draftKey(userId: string, org: string, project: string, kind: string) {
  return `nodus.draft.v1:${userId}:${org}:${project}:${kind}`;
}

function readDraft(key: string): Draft | undefined {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as Draft) : undefined;
  } catch {
    return undefined;
  }
}

function writeDraft(key: string, d: Draft | undefined) {
  try {
    if (d) localStorage.setItem(key, JSON.stringify(d));
    else localStorage.removeItem(key);
  } catch {
    // Drafts are a convenience: without storage the form still works for this visit.
  }
}

/** Exact placement follows its selected offerings; automatic placement depends on the whole catalog. */
function offeringsKey(items: readonly OfferingLike[] | undefined, manifest: Manifest): string {
  const names = (manifest.spec as { placement?: { offerings?: string[] } } | undefined)?.placement?.offerings;
  const selected = Array.isArray(names) && names.length ? new Set(names) : undefined;
  const relevant = (items ?? []).filter((o) => !selected || selected.has(o.metadata.name));
  return JSON.stringify(relevant.sort((a, b) => a.metadata.name.localeCompare(b.metadata.name)).map((o) =>
    [o.metadata.name, o.spec, o.status?.availability, o.status?.availableNow, o.status?.priceUSDPerHour]));
}

export function useCreateFlow({
  kind, scope, userId, initial, draftSource, estimateDelayMs = 500, supply, canEstimate, prepareManifest,
}: {
  kind: string;
  scope: { org: string; project: string };
  userId: string;
  initial: Manifest;
  draftSource?: string;
  estimateDelayMs?: number;
  supply?: readonly OfferingLike[];
  canEstimate?: (manifest: Manifest) => boolean;
  /** Resolve automatic defaults for both quote and create; the draft keeps explicit user choices separate. */
  prepareManifest?: (manifest: Manifest) => Manifest;
}) {
  const { client } = useApi();
  const key = draftKey(userId, scope.org, scope.project, kind) + (draftSource ? `:${draftSource}` : "");
  const [restored] = React.useState(() => readDraft(key));
  const [manifest, setManifestState] = React.useState<Manifest>(() => restored?.manifest ?? initial);
  const prepared = React.useMemo(() => prepareManifest?.(manifest) ?? manifest, [manifest, prepareManifest]);
  const [idempotencyKey, setIdempotencyKey] = React.useState(() => restored?.idempotencyKey ?? newIdempotencyKey());
  const [draftRestored, setDraftRestored] = React.useState(Boolean(restored));
  const [quote, setQuote] = React.useState<{ view?: EstimateView; etag?: string; loading: boolean; key?: string; error?: string; cause?: unknown }>({ loading: false });
  const [launch, setLaunch] = React.useState<LaunchState>(restored?.submitted ? { state: "uncertain" } : { state: "idle" });
  const [expired, setExpired] = React.useState(false);
  const seq = React.useRef(0);
  const sending = React.useRef(false);
  const attempted = React.useRef(Boolean(restored?.submitted));
  const submitted = React.useRef<Submission | undefined>(restored?.submitted);
  // Recomputing identical defaults must not replace the reviewed quote.
  const preparedJSON = JSON.stringify(prepared);
  const supplyKey = offeringsKey(supply, prepared);
  const quoteKey = JSON.stringify([preparedJSON, supplyKey]);
  const estimateEnabled = canEstimate?.(prepared) ?? true;
  const stale = !estimateEnabled || quote.key !== quoteKey || expired;
  const estimate = { ...quote, stale };
  // Block only a new request. An uncertain submission must retry its original body, ETag and key.
  const canStart = !stale && !quote.loading && Boolean(quote.etag) && !quote.view?.blockingReasons?.length;
  const canSubmit = launch.state !== "launching" && launch.state !== "done" && (Boolean(submitted.current) || canStart);

  const setManifest = React.useCallback((m: Manifest) => {
    // An uncertain create is retried with its original name, body and key.
    if (sending.current || submitted.current) return;
    if (JSON.stringify(m) === JSON.stringify(manifest)) return;
    ++seq.current;
    const nextKey = attempted.current ? newIdempotencyKey() : idempotencyKey;
    attempted.current = false;
    setIdempotencyKey(nextKey);
    setManifestState(m);
    setQuote({ loading: false });
    setLaunch({ state: "idle" });
    writeDraft(key, { manifest: m, idempotencyKey: nextKey, savedAt: new Date().toISOString() });
  }, [manifest, key, idempotencyKey]);

  const runEstimate = React.useCallback(async () => {
    if (!estimateEnabled || sending.current || submitted.current) return;
    const mine = ++seq.current;
    setExpired(false);
    setQuote({ loading: true });
    setLaunch((current) => current.state === "error" ? { state: "idle" } : current);
    try {
      const res = await client.request<KObject>("POST", collectionPath(kind, scope.project), {
        org: scope.org, query: { dryRun: "All" }, body: JSON.parse(preparedJSON) as Manifest,
      });
      if (mine !== seq.current) return;
      const view = (res.data.status as { estimate?: EstimateView } | undefined)?.estimate;
      setQuote({ loading: false, key: quoteKey, ...(view ? { view } : {}), ...(res.etag ? { etag: res.etag } : {}) });
    } catch (err) {
      if (mine !== seq.current) return;
      const message = err instanceof ApiError ? (err.status.message ?? `Estimate failed (${err.code})`) : "Could not estimate right now.";
      setQuote({ loading: false, error: message, cause: err });
    }
  }, [client, kind, scope.org, scope.project, preparedJSON, quoteKey, estimateEnabled]);

  React.useEffect(() => {
    const generation = seq;
    const t = setTimeout(() => void runEstimate(), estimateDelayMs);
    return () => { clearTimeout(t); ++generation.current; };
  }, [runEstimate, estimateDelayMs]);
  React.useEffect(() => {
    if (!quote.view?.validUntil) return;
    const delay = Date.parse(quote.view.validUntil) - Date.now();
    if (!Number.isFinite(delay)) return;
    const t = setTimeout(() => setExpired(true), Math.max(0, Math.min(delay, 2_147_483_647)));
    return () => clearTimeout(t);
  }, [quote.view?.validUntil]);

  const submit = React.useCallback(async () => {
    if (sending.current || launch.state === "done") return;
    const request = submitted.current ?? (canStart && quote.etag ? { manifest: structuredClone(prepared), idempotencyKey, etag: quote.etag } : undefined);
    if (!request) return;
    submitted.current = request;
    attempted.current = true;
    sending.current = true;
    writeDraft(key, { manifest, idempotencyKey: request.idempotencyKey, submitted: request, savedAt: new Date().toISOString() });
    setLaunch({ state: "launching" });
    try {
      const res = await client.request<KObject>("POST", collectionPath(kind, scope.project), {
        org: scope.org, body: request.manifest, idempotencyKey: request.idempotencyKey, ifMatch: request.etag,
      });
      writeDraft(key, undefined);
      setLaunch({ state: "done", object: res.data });
      return res.data;
    } catch (err) {
      if (classify(err, true) === "uncertain") {
        setLaunch({ state: "uncertain", ...(err instanceof ApiError && err.requestId ? { requestId: err.requestId } : {}) });
      } else {
        submitted.current = undefined;
        // A definite rejection did not create anything. A fresh key lets billing, permission or rate fixes take effect.
        const nextKey = newIdempotencyKey();
        setIdempotencyKey(nextKey);
        writeDraft(key, { manifest, idempotencyKey: nextKey, savedAt: new Date().toISOString() });
        if (err instanceof ApiError && err.code === 412) {
          setQuote({ loading: false });
          setLaunch({ state: "repriced" });
        } else setLaunch({ state: "error", error: err });
      }
      return undefined;
    } finally {
      sending.current = false;
    }
  }, [client, kind, scope.org, scope.project, manifest, prepared, idempotencyKey, quote, canStart, key, launch.state]);

  React.useEffect(() => { if (launch.state === "repriced") void runEstimate(); }, [launch.state, runEstimate]);

  const name = String(manifest.metadata?.name ?? "");
  const checkStatus = React.useCallback(async () => {
    try {
      const obj = await client.get<KObject>(objectPath(kind, name, scope.project), { org: scope.org });
      writeDraft(key, undefined);
      setLaunch({ state: "done", object: obj });
    } catch (error) {
      // A failed status lookup does not prove that the original create failed.
      setLaunch({ state: "uncertain", ...(error instanceof ApiError && error.requestId ? { requestId: error.requestId } : {}) });
    }
  }, [client, kind, name, scope.org, scope.project, key]);

  const discardDraft = React.useCallback(() => {
    if (sending.current || submitted.current) return;
    ++seq.current;
    writeDraft(key, undefined);
    setDraftRestored(false);
    setManifestState(initial);
    setIdempotencyKey(newIdempotencyKey());
    setQuote({ loading: false });
    setLaunch({ state: "idle" });
  }, [key, initial]);

  return { manifest, prepared: submitted.current?.manifest ?? prepared, setManifest, estimate, canSubmit, reestimate: runEstimate, launch, submit, retry: submit, checkStatus, idempotencyKey, draftRestored, discardDraft };
}

/**
 * Duplicate (`?from=job/lora-fine-tune`): loads the source object and returns it as a new manifest (spec and user
 * labels, a fresh generated name). Undefined while loading or when there is no source.
 */
export function useDuplicateSource(from: string | undefined, scope: { org: string; project: string }, expectedKind: string, refreshSource = false): { manifest?: Manifest; original?: KObject; loading: boolean; error?: unknown; retry: () => void } {
  const { client } = useApi();
  const [short, name] = (from ?? "").split("/", 2) as [string, string | undefined];
  const kind = from ? kindByShortName(short)?.kind : undefined;
  const q = useQuery({
    queryKey: [scope.org, scope.project, "duplicate", from],
    queryFn: () => client.get<KObject>(objectPath(kind!, name!, scope.project), { org: scope.org }),
    enabled: Boolean(kind && name && kind === expectedKind),
    refetchInterval: refreshSource ? 5_000 : false,
  });
  const retry = () => { if (kind === expectedKind && name) void q.refetch(); };
  if (!from) return { loading: false, retry };
  if (!kind || !name || kind !== expectedKind) return { loading: false, error: new Error(`Choose an existing ${expectedKind} to run again.`), retry };
  return { loading: q.isLoading, error: q.error, retry, ...(q.data ? { manifest: duplicateOf(q.data as unknown as Manifest, generatedName(name)), original: q.data } : {}) };
}
